import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useMsal } from '@azure/msal-react';
import { useAppSelector } from '../../store/hooks';
import { useTheme } from '../Theme/ThemeProvider';
import { submitConversationFeedback, ConversationFeedbackRequest } from '../../utils/conversationFeedback.service';
import { ChatMessage } from '../../utils/chat.service';

interface MessageFeedbackProps {
  messageIndex: number;
  message: ChatMessage;
  chatHistory: Array<{ role?: string; content?: string | any[] }>;
  onFeedbackSubmitted?: () => void;
  onFeedbackFailed?: () => void;
}

/** Fixed set of negative-feedback reason chips shown in the reason panel. */
const FEEDBACK_REASONS = ['Inaccurate', 'Wrong Sources', 'Too long', 'Too Short', 'Other'];

/**
 * Derives the RAG type string sent with feedback.
 * In '3M Knowledge' mode the active ragChatType is encoded as a
 * screaming-snake-case `RAG_*` identifier; all other modes map to 'ASK_CHAT'.
 */
function deriveRagType(mode: string, ragChatType: string | null): string {
  if (mode === '3M Knowledge' && ragChatType) {
    return `RAG_${ragChatType.toUpperCase().replace(/[&\s]+/g, '_')}`;
  }
  return 'ASK_CHAT';
}

/**
 * Normalises a message's content field to a plain string.
 * Content can be a bare string (text-only messages) or an array of
 * multi-modal content items (e.g. `{ type: 'input_text', text: '...' }`).
 * Only `input_text` items contribute to the extracted text.
 */
function extractTextContent(content: string | any[]): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter((ci: any) => ci.type === 'input_text')
      .map((ci: any) => ci.text || '')
      .join('\n')
      .trim();
  }
  return '';
}

/**
 * Inline thumbs-up / thumbs-down feedback widget rendered below each assistant message.
 * A thumbs-up immediately submits a positive rating. A thumbs-down opens a reason
 * panel where the user selects a chip (and optionally types free text) before submitting.
 */
const MessageFeedback: React.FC<MessageFeedbackProps> = ({ messageIndex, message, chatHistory, onFeedbackSubmitted, onFeedbackFailed }) => {
  /** Which rating button the user has confirmed ('thumbs_up' | 'thumbs_down' | null = unrated). */
  const [rating, setRating] = useState<'thumbs_up' | 'thumbs_down' | null>(null);
  /** True while an API call to submit feedback is in-flight. */
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Controls visibility of the negative-feedback reason panel (chip row + optional text area + send). */
  const [isReasonPanelOpen, setIsReasonPanelOpen] = useState(false);
  /** The currently highlighted reason chip, or null if none selected yet. */
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  /** Free-text input value; only relevant when selectedReason === 'Other'. */
  const [customReasonText, setCustomReasonText] = useState('');
  /** Whether the user opted-in to sharing their query + response alongside the feedback. */
  const [shouldShareConversation, setShouldShareConversation] = useState(false);

  /**
   * Ref that wraps both the thumb buttons and the floating reason panel so that a
   * single mousedown listener can close the panel when clicking outside either element.
   */
  const menuRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { instance: msalInstance } = useMsal();
  const { mode } = useTheme();
  const chatModeState = useAppSelector((state) => state.chatMode);

  /**
   * Attaches a document-level mousedown listener only while the reason panel is open.
   * Clicks outside `menuRef` (i.e. outside both the buttons and the panel) dismiss the
   * panel and reset its transient state — but deliberately do NOT reset `rating`, so a
   * previously submitted positive rating remains visually active.
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (!menuRef.current || !menuRef.current.contains(target)) &&
        (!panelRef.current || !panelRef.current.contains(target))
      ) {
        setIsReasonPanelOpen(false);
        setSelectedReason(null);
        setCustomReasonText('');
        setShouldShareConversation(false);
      }
    };
    // Only register the listener when the panel is actually visible to avoid
    // unnecessary global event overhead on every rendered message.
    if (isReasonPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isReasonPanelOpen]);

  /** Pre-computed RAG type derived from the current chat mode; only recalculated when the mode changes. */
  const ragType = useMemo(
    () => deriveRagType(chatModeState.mode, chatModeState.ragChatType),
    [chatModeState.mode, chatModeState.ragChatType]
  );

  /**
   * Positions the portal panel by measuring its actual rendered height and writing
   * `top`/`bottom`/`left` directly onto the DOM node — before the browser paints,
   * so the panel is never visible in the wrong position. Runs on open and whenever
   * the panel height changes (e.g. when the 'Other' textarea expands).
   */
  useLayoutEffect(() => {
    if (!isReasonPanelOpen || !panelRef.current || !menuRef.current) return;
    const panel = panelRef.current;
    const panelHeight = panel.getBoundingClientRect().height;
    const rect = menuRef.current.getBoundingClientRect();
    const HEADER_HEIGHT = 80;
    const GAP = 8;
    const spaceAbove = rect.top - HEADER_HEIGHT - GAP;
    panel.style.left = `${rect.left + 36}px`;
    if (spaceAbove >= panelHeight) {
      panel.style.top = '';
      panel.style.bottom = `${window.innerHeight - rect.top + GAP}px`;
    } else {
      panel.style.bottom = '';
      panel.style.top = `${rect.bottom + GAP}px`;
    }
    panel.style.visibility = 'visible';
  }, [isReasonPanelOpen, selectedReason]);

  /**
   * Sends the feedback payload to the API.
   * When `includeConversation` is true the most-recent user query (found by walking
   * backwards through `chatHistory`) and the current assistant message are attached;
   * otherwise both fields are left empty for anonymous ratings.
   */
  const submitFeedback = useCallback(async (
    selectedRating: 'thumbs_up' | 'thumbs_down',
    feedbackText: string,
    includeConversation: boolean = false
  ) => {
    setIsSubmitting(true);
    try {
      let userQuery = '';
      let assistantResponse = '';

      if (includeConversation) {
        for (let i = messageIndex - 1; i >= 0; i--) {
          if (chatHistory[i]?.role === 'user') {
            userQuery = extractTextContent(chatHistory[i].content || '');
            break;
          }
        }
        assistantResponse = extractTextContent(message.content);
      }

      const request: ConversationFeedbackRequest = {
        query: userQuery,
        response: assistantResponse,
        rating: selectedRating,
        feedback: feedbackText,
        rag_type: ragType,
      };

      await submitConversationFeedback(msalInstance, request);
      onFeedbackSubmitted?.();
    } catch (error) {
      console.error('[MessageFeedback] Failed to submit feedback:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  }, [messageIndex, chatHistory, message, ragType, msalInstance, onFeedbackSubmitted]);

  /**
   * Handles the thumbs-up button. If already rated thumbs-up, clicking again
   * un-rates the message (optimistic toggle without a server call). Otherwise
   * submits a positive rating immediately without opening the reason panel.
   */
  const handleThumbsUp = useCallback(async () => {
    if (isSubmitting) return;
    if (rating === 'thumbs_up') {
      setRating(null);
      return;
    }
    setRating('thumbs_up');
    setIsReasonPanelOpen(false);
    setSelectedReason(null);
    try {
      await submitFeedback('thumbs_up', 'User liked the generated response', false);
    } catch {
      setRating(null);
      onFeedbackFailed?.();
    }
  }, [isSubmitting, rating, submitFeedback, onFeedbackFailed]);

  /**
   * Handles the thumbs-down button. Unlike thumbs-up, clicking does NOT immediately
   * commit the rating — it only opens the reason panel. The rating is committed to
   * `thumbs_down` only after the user submits a reason via `handleFeedbackFormSubmit`.
   * Re-clicking while the panel is closed resets it; re-clicking while the panel is
   * open is a no-op (avoids toggling the panel accidentally).
   */
  const handleThumbsDown = useCallback(() => {
    if (isSubmitting) return;
    // Allow un-rating if the panel is currently closed (rating was already committed).
    if (rating === 'thumbs_down' && !isReasonPanelOpen) {
      setRating(null);
      return;
    }
    setIsReasonPanelOpen(true);
    if(chatModeState.ragChatType === 'HR') {
      setSelectedReason('Other');
    } else {
      setSelectedReason(null);
    }
    setCustomReasonText('');
    setShouldShareConversation(false);
  }, [isSubmitting, rating, isReasonPanelOpen, chatModeState.ragChatType]);

  /** Selects (or re-selects) a reason chip in the reason panel. */
  const handleReasonSelect = useCallback((reason: string) => {
    setSelectedReason(reason);
  }, []);

  /**
   * Submits the negative feedback form. Guards against empty state and in-flight
   * requests. The 'Other' reason requires non-empty free text before submission.
   */
  const handleFeedbackFormSubmit = useCallback(async () => {
    if (!selectedReason || isSubmitting) return;
    if (selectedReason === 'Other' && !customReasonText.trim()) return;
    const feedbackText = selectedReason === 'Other' ? customReasonText.trim() : selectedReason;
    setRating('thumbs_down');
    setIsReasonPanelOpen(false);
    setSelectedReason(null);
    setCustomReasonText('');
    setShouldShareConversation(false);
    try {
      await submitFeedback('thumbs_down', feedbackText, shouldShareConversation);
    } catch {
      setRating(null);
      onFeedbackFailed?.();
    }
  }, [selectedReason, isSubmitting, customReasonText, shouldShareConversation, submitFeedback, onFeedbackFailed]);

  return (
    <div className="relative flex items-center gap-1" ref={menuRef}>
      <button
        onClick={handleThumbsUp}
        disabled={isSubmitting}
        className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] ${
          rating === 'thumbs_up'
            ? 'text-blue-500'
            : 'text-[var(--color-text)] hover:opacity-70'
        }`}
        title="Thumbs up"
      >
        {isSubmitting && rating === 'thumbs_up' ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 23 24" fill="none">
            <path d="M20.8438 11.25C20.8438 10.0125 19.8734 9 18.6875 9H13.8287C14.0516 7.8525 14.375 5.8125 14.375 3.75C14.375 2.5125 13.4047 1.5 12.2188 1.5C11.0328 1.5 10.0625 2.5125 10.0625 3.75C10.0625 7.02 7.54688 10.5 2.875 10.5C2.47969 10.5 2.15625 10.8375 2.15625 11.25V20.25C2.15625 20.6625 2.47969 21 2.875 21C4.79406 21 6.30344 21.4575 7.64031 21.8625C8.71844 22.1925 9.73906 22.5 10.7812 22.5H16.5312C17.7172 22.5 18.6875 21.4875 18.6875 20.25C18.6875 19.845 18.5797 19.4775 18.4 19.1475C19.0037 18.75 19.4062 18.0525 19.4062 17.25C19.4062 16.845 19.2984 16.4775 19.1187 16.1475C19.7225 15.75 20.125 15.0525 20.125 14.25C20.125 13.845 20.0172 13.4775 19.8375 13.1475C20.4412 12.75 20.8438 12.0525 20.8438 11.25ZM16.5312 21H10.7812C9.94031 21 9.06344 20.73 8.03563 20.4225C6.77781 20.04 5.36906 19.6125 3.59375 19.5225V11.9775C8.73281 11.6325 11.5 7.575 11.5 3.75C11.5 3.3375 11.8234 3 12.2188 3C12.6141 3 12.9375 3.3375 12.9375 3.75C12.9375 6.615 12.2475 9.54 12.2403 9.57C12.19 9.795 12.2403 10.035 12.3697 10.215C12.5063 10.395 12.7147 10.5 12.9375 10.5H18.6875C19.0828 10.5 19.4062 10.8375 19.4062 11.25C19.4062 11.6625 19.0828 12 18.6875 12H15.0938V13.5H17.9688C18.3641 13.5 18.6875 13.8375 18.6875 14.25C18.6875 14.6625 18.3641 15 17.9688 15H14.375V16.5H17.25C17.6453 16.5 17.9688 16.8375 17.9688 17.25C17.9688 17.6625 17.6453 18 17.25 18H13.6562V19.5H16.5312C16.9266 19.5 17.25 19.8375 17.25 20.25C17.25 20.6625 16.9266 21 16.5312 21Z" fill="currentColor"/>
          </svg>
        )}
      </button>
      <button
        onClick={handleThumbsDown}
        disabled={isSubmitting}
        className={`w-8 h-8 flex items-center justify-center rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[var(--color-accent)] ${
          rating === 'thumbs_down'
            ? 'text-blue-500'
            : 'text-[var(--color-text)] hover:opacity-70'
        }`}
        title="Thumbs down"
      >
        {isSubmitting && rating === 'thumbs_down' ? (
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 28 28" fill="none">
            <path d="M2.625 14.875C2.625 16.3188 3.80625 17.5 5.25 17.5H11.165C10.8938 18.8388 10.5 21.2188 10.5 23.625C10.5 25.0688 11.6812 26.25 13.125 26.25C14.5688 26.25 15.75 25.0688 15.75 23.625C15.75 19.81 18.8125 15.75 24.5 15.75C24.9813 15.75 25.375 15.3562 25.375 14.875V4.375C25.375 3.89375 24.9813 3.5 24.5 3.5C22.1637 3.5 20.3263 2.96625 18.6988 2.49375C17.3863 2.10875 16.1438 1.75 14.875 1.75H7.875C6.43125 1.75 5.25 2.93125 5.25 4.375C5.25 4.8475 5.38125 5.27625 5.6 5.66125C4.865 6.125 4.375 6.93875 4.375 7.875C4.375 8.3475 4.50625 8.77625 4.725 9.16125C3.99 9.625 3.5 10.4388 3.5 11.375C3.5 11.8475 3.63125 12.2762 3.85 12.6613C3.115 13.125 2.625 13.9388 2.625 14.875ZM7.875 3.5H14.875C15.8988 3.5 16.9662 3.815 18.2175 4.17375C19.7487 4.62 21.4638 5.11875 23.625 5.22375V14.0262C17.3687 14.4288 14 19.1625 14 23.625C14 24.1063 13.6062 24.5 13.125 24.5C12.6438 24.5 12.25 24.1063 12.25 23.625C12.25 20.2825 13.09 16.87 13.0988 16.835C13.16 16.5725 13.0988 16.2925 12.9412 16.0825C12.775 15.8725 12.5212 15.75 12.25 15.75H5.25C4.76875 15.75 4.375 15.3562 4.375 14.875C4.375 14.3938 4.76875 14 5.25 14H9.625V12.25H6.125C5.64375 12.25 5.25 11.8562 5.25 11.375C5.25 10.8938 5.64375 10.5 6.125 10.5H10.5V8.75H7C6.51875 8.75 6.125 8.35625 6.125 7.875C6.125 7.39375 6.51875 7 7 7H11.375V5.25H7.875C7.39375 5.25 7 4.85625 7 4.375C7 3.89375 7.39375 3.5 7.875 3.5Z" fill="currentColor"/>
          </svg>
        )}
      </button>


      {isReasonPanelOpen && createPortal(
          <div
            ref={panelRef}
            className="flex flex-col rounded-lg shadow-lg border overflow-hidden min-w-[380px]"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              visibility: 'hidden',
              zIndex: 9999,
              animation: 'feedbackMenuIn 200ms ease-out',
              backgroundColor: mode === 'dark' ? '#313136' : 'var(--color-surface)',
              borderColor: mode === 'dark' ? '#4a4a52' : 'var(--color-border)',
            }}
          >
            <div className="py-2.5 flex flex-row flex-nowrap gap-1.5 px-3">
              {FEEDBACK_REASONS.map((reason) => (
                <button
                  key={reason}
                  onClick={() => handleReasonSelect(reason)}
                  className={`whitespace-nowrap text-xs px-2.5 py-1 rounded-full border transition-all duration-150 ${
                    selectedReason === reason
                      ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-white font-semibold shadow-md scale-[1.04]'
                      : 'border-[var(--color-border)] bg-transparent text-[var(--color-text)] shadow-sm hover:shadow hover:border-[var(--color-accent)]/50 hover:bg-[var(--color-hover)]/40'
                  }`}
                >
                  {reason}
                </button>
              ))}
            </div>

            {selectedReason === 'Other' && (
              <div
                className="px-3 pb-3 border-t border-[var(--color-border)] overflow-hidden"
                style={{ animation: 'feedbackExpandIn 220ms ease-out' }}
              >
                <p className="text-sm text-[var(--color-text)] mt-3 mb-2">
                  What didn't you like about this response?
                </p>
                <textarea
                  value={customReasonText}
                  onChange={(e) => setCustomReasonText(e.target.value)}
                  placeholder="Do not include any sensitive or personal information."
                  className="w-full h-20 px-3 py-2 text-sm rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] placeholder-gray-500 resize focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
                  style={{ minHeight: '80px', maxHeight: '200px', minWidth: '360px', maxWidth: '400px' }}
                />
              </div>
            )}

            <div className="flex items-center justify-between px-3 py-2.5 border-t border-[var(--color-border)]">
              {chatModeState.ragChatType === 'HR' ? (
              <div style={{ maxWidth: '240px' }} className="text-xs text-[var(--color-text)]">
                If you require more assistance, submit an <a target="_blank" rel="noopener noreferrer" href="https://3m3.my.site.com/globalcommunity/s/createrecord/NewHRrequest" className="text-blue-600 hover:underline">HR Help ticket</a>.
              </div>
              )
              : null}
              {/* Checkbox temporarily hidden — wording to be confirmed before re-enabling.
              <label className="flex items-center gap-1 cursor-pointer text-sm text-[var(--color-text)]">
                <input
                  type="checkbox"
                  checked={shouldShareConversation}
                  onChange={(e) => setShouldShareConversation(e.target.checked)}
                  className="rounded border-[var(--color-border)] accent-blue-600"
                />
                <span className="mt-1">Share my query and response</span>
              </label>
              */}
              <button
                onClick={handleFeedbackFormSubmit}
                disabled={!selectedReason || (selectedReason === 'Other' && !customReasonText.trim()) || isSubmitting}
                className="ml-auto px-4 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 shrink-0"
              >
                {isSubmitting ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        , document.body
      )}
    </div>
  );
};

export default MessageFeedback;